// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { SourceMapV3 } from './mappings';

/**
 * Index maps (the `sections` field).
 *
 * AN INDEX MAP HAS NO TOP-LEVEL `mappings`, `sources` OR `sourcesContent`. So the natural line —
 *
 *     (map.mappings || '').split(';')
 *
 * — yields zero segments, zero sources and a clean "no sourcemap leaks detected" report WHILE THE
 * MAP IS HANDING OUT THE ENTIRE SOURCE TREE. That is a false negative in a security tool, which is
 * why `Array.isArray(map.sections)` is tested FIRST, before any other field is read.
 *
 * The rarity argument for ignoring them is stale on every count: esbuild 0.28.1 FLATTENS index maps
 * rather than rejecting them (the widely-cited "Source maps with 'sections' are not supported"
 * warning does not appear in the 0.28.1 binary at all, and issue #3439 is CLOSED).
 *
 * THE SPLIT WE SHIP: refuse to produce any file:line:column from an index map — loudly, by name —
 * but still recover `sourcesContent` from every section, because that path involves no VLQ at all.
 *
 * Why refuse rather than half-decode: the correct offset semantics are `finalLine = offset.line +
 * innerLine` ALWAYS, but `finalColumn = innerColumn + offset.column` ONLY WHEN `innerLine === 0`.
 * Applying `offset.column` to every line is a silent per-line shift — a decoder that points at the
 * wrong original line with total confidence, which is the single worst thing this tool can do.
 */

const MAX_DEPTH = 10;

export interface Section {
  offset?: { line?: number; column?: number };
  map?: SourceMapV3;
}

export function isIndexMap(map: unknown): boolean {
  return typeof map === 'object' && map !== null && Array.isArray((map as SourceMapV3).sections);
}

export interface RecoveredSource {
  readonly source: string;
  readonly content: string;
}

/**
 * Pull every `(source, sourcesContent)` pair out of a map, walking into an index map's sections.
 * Works for both plain and index maps so callers have one path.
 */
export function extractSources(map: SourceMapV3, depth = 0): RecoveredSource[] {
  if (depth > MAX_DEPTH) {
    throw new Error('index map nested more than 10 deep — treating as malformed rather than recursing');
  }
  const out: RecoveredSource[] = [];

  if (isIndexMap(map)) {
    for (const raw of (map.sections ?? []) as Section[]) {
      if (!raw || typeof raw !== 'object' || !raw.map) continue;
      out.push(...extractSources(raw.map, depth + 1));
    }
    return out;
  }

  const sources = map.sources ?? [];
  const contents = map.sourcesContent ?? [];
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    const c = contents[i];
    // A null or absent entry is spec-legal and means "no content shipped for this source". It is not
    // an error and it is not a finding — it is the good case.
    if (typeof s !== 'string' || typeof c !== 'string') continue;
    out.push({ source: s, content: c });
  }
  return out;
}
